import { FC, useEffect, useMemo } from 'react';
import {
  isMaxBelowMarket,
  isMinAboveMarket,
} from 'components/strategies/overlapping/utils';
import { OverlappingBudget } from 'components/strategies/overlapping/OverlappingBudget';
import { SafeDecimal } from 'libs/safedecimal';
import {
  BudgetDescription,
  BudgetDistribution,
} from 'components/strategies/common/BudgetDistribution';
import { Token } from 'libs/tokens';
import { CreateOverlappingOrder } from 'components/strategies/common/types';
import { isValidRange } from '../utils';
import {
  StrategyDirection,
  TradeOverlappingSearch,
} from 'libs/routing/routes/trade';
import { UseQueryResult } from '@tanstack/react-query';

interface Props {
  base: Token;
  quote: Token;
  buy: CreateOverlappingOrder;
  sell: CreateOverlappingOrder;
  anchor?: StrategyDirection;
  budget?: string;
  baseBalance?: UseQueryResult<string, Error>;
  quoteBalance?: UseQueryResult<string, Error>;
  set: (next: TradeOverlappingSearch) => any;
}

export const CreateOverlappingBudget: FC<Props> = (props) => {
  const {
    base,
    quote,
    buy,
    sell,
    anchor,
    budget,
    baseBalance,
    quoteBalance,
    set,
  } = props;

  const balanceQuery = anchor === 'buy' ? quoteBalance : baseBalance;
  const aboveMarket = isMinAboveMarket(buy);
  const belowMarket = isMaxBelowMarket(sell);

  // ERROR
  const budgetError = useMemo(() => {
    if (anchor === 'buy' && quoteBalance?.data && budget) {
      const balance = quoteBalance.data;
      const hasError = new SafeDecimal(budget).gt(balance);
      if (hasError) return 'Insufficient balance';
    }
    if (anchor === 'sell' && baseBalance?.data && budget) {
      const balance = baseBalance.data;
      const hasError = new SafeDecimal(budget).gt(balance);
      if (hasError) return 'Insufficient balance';
    }
  }, [anchor, baseBalance, budget, quoteBalance]);

  useEffect(() => {
    if (!isValidRange(buy.min, sell.max)) return;
    if (anchor === 'buy' && aboveMarket) {
      set({ anchor: 'sell', budget: undefined });
    }
    if (anchor === 'sell' && belowMarket) {
      set({ anchor: 'buy', budget: undefined });
    }
  }, [anchor, aboveMarket, belowMarket, set, buy.min, sell.max]);

  const setBudget = async (budget: string) => set({ budget });

  if (!anchor) return null;

  if (!base || !quote) return null;

  return (
    <>
      <article className="grid gap-16 p-16">
        <OverlappingBudget
          base={base}
          quote={quote}
          anchor={anchor}
          editType="deposit"
          budget={budget ?? ''}
          balanceQuery={balanceQuery}
          setBudget={setBudget}
          error={budgetError}
        />
      </article>
      <article id="overlapping-distribution" className="grid gap-16 p-16">
        <hgroup>
          <h3 className="text-16 font-medium flex items-center gap-8">
            Distribution
          </h3>
          <p className="text-14 text-main-0/80">
            Based on the deposit amount above, the total required budgets are as
            follows:
          </p>
        </hgroup>
        <BudgetDistribution
          title="Sell"
          token={base}
          initialBudget="0"
          withdraw="0"
          deposit={budgetError ? '0' : sell.budget}
          balanceQuery={baseBalance}
        />
        {baseBalance?.isEnabled && (
          <BudgetDescription
            token={base}
            initialBudget="0"
            withdraw="0"
            deposit={budgetError ? '0' : sell.budget}
            balance={baseBalance?.data || '0'}
          />
        )}
        <BudgetDistribution
          title="Buy"
          token={quote}
          initialBudget="0"
          withdraw="0"
          deposit={budgetError ? '0' : buy.budget}
          balanceQuery={quoteBalance}
          isBuy
        />
        {quoteBalance?.isEnabled && (
          <BudgetDescription
            token={quote}
            initialBudget="0"
            withdraw="0"
            deposit={budgetError ? '0' : buy.budget}
            balance={quoteBalance?.data || '0'}
          />
        )}
      </article>
    </>
  );
};
